/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSuccess,
  AssetOption,
  Ducks,
  Folder,
  FolderPurchaseOption,
  OptionUtils,
  Percentage,
  ProductPriceStatus,
  PurchaseOptionType,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLeading } from 'redux-saga/effects';
import Options from 'core/store/ducks/pi/options';
import { StoreState } from '../../store';
import translate from '../../../services/i18n/translate';
import PricesUtils from '../../../services/utils/pricesUtils';

export enum ActionType {
  REQUEST = 'app/folderInfo/options/REQUEST',
  SUCCESS = 'app/folderInfo/options/SUCCESS',
}

type Request = ActionRequest<ActionType.REQUEST, Folder>;
type Success = ActionSuccess<ActionType.SUCCESS, AssetOption[]>;

type Actions = Request | Success;

const Actions = {
  request: (folder: Folder): Request => ({ type: ActionType.REQUEST, meta: folder }),
  success: (options: AssetOption[]): Success => ({ type: ActionType.SUCCESS, payload: options }),
};

export const ActionCreators = {
  request: Actions.request,
};

type ReducerState = {
  data: AssetOption[];
};

const initialState: ReducerState = {
  data: [],
};

export default function currentFolder(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.REQUEST: {
      return initialState;
    }
    case ActionType.SUCCESS: {
      return {
        data: action.payload,
      };
    }
    default:
      return state;
  }
}

export const Selectors = {
  getOptions: (state: StoreState) => state.folderInfo.options.data,
};

function setOptionTitles(options: FolderPurchaseOption[], vat: Percentage): FolderPurchaseOption[] {
  return options.map((option) => {
    if (
      option.productPrice.status === ProductPriceStatus.FREE ||
      option.productPrice.status === ProductPriceStatus.PURCHASED
    ) {
      return {
        ...option,
        title: translate['watchtv.subscribed'],
      };
    }

    const finalPrice = {
      ...option.productPrice.price,
      amount: OptionUtils.calcFinalPrice(option.productPrice.price.amount, vat),
    };

    const priceAsStr = PricesUtils.formatPrice(finalPrice);

    let titleTemplate = translate['buttons.subscription'];
    if (OptionUtils.isBundleOption(option)) {
      titleTemplate =
        option.purchaseOptionType === PurchaseOptionType.BUY
          ? translate['buttons.ownBoxSet']
          : translate['buttons.rentBoxSet'];
    }
    return {
      ...option,
      title: translate.formatString(titleTemplate, { price: priceAsStr }).toString(),
    };
  });
}

export function* getOptions(request: Request): SagaIterator {
  const { meta } = request;

  let options: FolderPurchaseOption[] = [];

  const vat: Percentage | undefined = yield select(Ducks.Household.Selectors.data.vat);

  // if vat is undefined then we shall not calculate options
  if (vat !== undefined) {
    // when vat is not supported the value is 0
    options = yield call(Options.getFolderPurchasesOptions, [meta], true);
    options = setOptionTitles(options, vat);
  }

  yield put(Actions.success(options));
}

export function* sagas() {
  yield all([takeLeading(ActionType.REQUEST, getOptions)]);
}
